import {
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from 'react-query';
import { createCrypto, CreateCryptoData } from 'src/Api/cryptos';
import { CRYPTOS } from 'src/Api/endpoints';
import { toast } from 'react-toastify';

const formSchema = yup.object({
  name: yup.string().required('Name is required'),
  symbol: yup.string().required('Symbol is required').uppercase(),
  slug: yup.string().optional()
});

type FormSchemaType = yup.InferType<typeof formSchema>;

export const CreateCryptoForm = () => {
  const queryClient = useQueryClient();

  const createMutation = useMutation((data: CreateCryptoData) => {
    return createCrypto(data);
  });

  const form = useForm<FormSchemaType>({
    defaultValues: {
      name: '',
      symbol: '',
      slug: ''
    },
    resolver: yupResolver(formSchema),
    mode: 'onChange'
  });

  const onSubmit = (formData: FormSchemaType) => {
    createMutation
      .mutateAsync({
        name: formData.name,
        symbol: formData.symbol.toUpperCase(),
        slug: formData.slug || formData.symbol.toLowerCase()
      })
      .then((crypto) => {
        queryClient.invalidateQueries([CRYPTOS]);
        toast(`${crypto.symbol} created successfully!`, {
          theme: 'dark',
          type: 'success',
          autoClose: 5000
        });
        form.reset();
      })
      .catch((error) => {
        toast(`Error: ${error?.response?.data?.message || error?.message}`, {
          theme: 'dark',
          type: 'error',
          autoClose: 8000
        });
      });
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 500 }}>
      <Typography variant="h6" mb={2}>
        Create New Crypto
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Use this form to create a cryptocurrency that is not listed on
        CoinMarketCap. Once created, you can map exchange symbols to this
        crypto.
      </Typography>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <TextField
            variant="outlined"
            type="text"
            size="small"
            label="Name"
            placeholder="e.g., Bitcoin"
            error={!!form.formState.errors.name}
            helperText={form.formState.errors.name?.message}
            {...form.register('name')}
          />

          <TextField
            variant="outlined"
            type="text"
            size="small"
            label="Symbol"
            placeholder="e.g., BTC"
            error={!!form.formState.errors.symbol}
            helperText={form.formState.errors.symbol?.message}
            {...form.register('symbol')}
          />

          <TextField
            variant="outlined"
            type="text"
            size="small"
            label="Slug (optional)"
            placeholder="e.g., bitcoin"
            helperText="URL-friendly identifier. Auto-generated from symbol if empty."
            {...form.register('slug')}
          />

          <Button
            disabled={!form.formState.isValid || createMutation.isLoading}
            variant="contained"
            color="primary"
            type="submit"
          >
            {createMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              'Create Crypto'
            )}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
};

